/**
 * vPedal Javascript Browser API adapter.
 * Tray app injects Ctrl+Alt+Shift+Digit1–7 into the focused browser.
 *
 * Mapping (from vPedal programmer notes):
 *  Digit1 → play down
 *  Digit2 → play up (stop)
 *  Digit3 → rewind down
 *  Digit4 → rewind up
 *  Digit5 → fast-forward down
 *  Digit6 → fast-forward up
 *  Digit7 → heartbeat (ignored for transport)
 *
 * @see https://www.vpedal.com/vpedal-api/
 */

/** @typedef {(event: { action: string, phase: 'down' | 'up' | 'change', raw?: string }) => void} PedalEventHandler */

const CHORD_MAP = {
    Digit1: { action: "play", phase: "down" },
    Digit2: { action: "play", phase: "up" },
    Digit3: { action: "rewind", phase: "down" },
    Digit4: { action: "rewind", phase: "up" },
    Digit5: { action: "fastForward", phase: "down" },
    Digit6: { action: "fastForward", phase: "up" },
};

/**
 * @param {{ onPedalEvent: PedalEventHandler }} options
 */
export function createVPedalBrowserAdapter({ onPedalEvent }) {
    /** @type {((e: KeyboardEvent) => void) | null} */
    let onKeyDown = null;

    function isVPedalChord(e) {
        return e.altKey && e.ctrlKey && e.shiftKey;
    }

    function handleKeyDown(e) {
        if (!isVPedalChord(e)) return;

        const mapped = CHORD_MAP[e.code];
        if (!mapped) {
            // Digit7 heartbeat or other — swallow chord so it doesn't type
            if (e.code === "Digit7") {
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        onPedalEvent({
            action: mapped.action,
            phase: mapped.phase,
            raw: e.code,
        });
    }

    return {
        async start() {
            onKeyDown = handleKeyDown;
            window.addEventListener("keydown", onKeyDown, true);
        },

        async stop() {
            if (onKeyDown) {
                window.removeEventListener("keydown", onKeyDown, true);
            }
            onKeyDown = null;
        },
    };
}

export const VPEDAL_CHORD_MAP = CHORD_MAP;
